import type { StorefrontSizeToken } from "./appearance";

/**
 * RAIL LAYOUT MATH
 *
 * Pure functions, no React/DOM: the hook measures, this decides. Separated
 * because these rules are the hardest appearance piece to get right, and each
 * proof needs only a number table — not a fake tree and ResizeObserver.
 *
 * Core rule: section size is a COUNT, not a px width — "compact is five per
 * view, medium four, large three". Width comes from dividing the column.
 */

/**
 * Whole cards per view on desktop. This table defines each size; everything
 * else follows from it.
 */
export const RAIL_PER_VIEW: Record<StorefrontSizeToken, number> = {
  compact: 5,
  medium: 4,
  large: 3,
};

/** Gap between cards: the storefront rails' existing `gap-3`. */
export const RAIL_GAP_REM = 0.75;

/** Soft edge mask on each end — carousel FADE. */
export const RAIL_FADE_REM = 2;

/**
 * Mobile card width, where the rule differs: no column split; the rail scrolls
 * and the peek of the next card signals more items.
 *
 * `medium` is today's reorder `w-[18rem]` — the table anchor. Compact/large
 * keep the same column ratios (`18 × 4/5` and `18 × 4/3`).
 */
export const RAIL_MOBILE_CARD_REM: Record<StorefrontSizeToken, number> = {
  compact: 18 * (4 / 5),
  medium: 18,
  large: 18 * (4 / 3),
};

/**
 * Rail card height in `rem`.
 *
 * `medium` is the reorder card's `h-[7.5rem]` — the no-op default. FIXED height
 * per size (not content-derived): side-by-side cards must stay aligned when a
 * name wraps.
 */
export const RAIL_CARD_HEIGHT_REM: Record<StorefrontSizeToken, number> = {
  compact: 7.5 * (4 / 5),
  medium: 7.5,
  large: 7.5 * (4 / 3),
};

/**
 * Rail card photo side in `rem`. `medium` is the reorder collage `w-[6.25rem]`.
 */
export const RAIL_CARD_PHOTO_REM: Record<StorefrontSizeToken, number> = {
  compact: 6.25 * (4 / 5),
  medium: 6.25,
  large: 6.25 * (4 / 3),
};

/**
 * Card height and photo — size `rem` values as px.
 *
 * Column count is a separate calculation. This is BOX SIZE: compact smaller,
 * medium the anchor, large bigger. Without it merchants saw columns change
 * while the card stayed the same size.
 */
export function railCardChrome(
  _widthPx: number,
  size: StorefrontSizeToken,
  rootFontSize = 14,
): { heightPx: number; photoPx: number } {
  return {
    heightPx: RAIL_CARD_HEIGHT_REM[size] * rootFontSize,
    photoPx: RAIL_CARD_PHOTO_REM[size] * rootFontSize,
  };
}

/**
 * Internal rail-card type and padding in `rem`.
 *
 * Box size already followed section size; text did not — name inherited body
 * and `ItemOfferPrice` hard-coded `18px`. These numbers are the mockup
 * `RAIL_SCALE` without `CONTENT_BOOST` (that 10% is preview-only).
 *
 * `medium` anchors on today's reorder: pad `p-2.5`, price `text-base`, CTA
 * `text-sm`.
 */
export const RAIL_NAME_REM: Record<StorefrontSizeToken, number> = {
  compact: 0.75,
  medium: 0.875,
  large: 1,
};

export const RAIL_PRICE_REM: Record<StorefrontSizeToken, number> = {
  compact: 0.875,
  medium: 1,
  large: 1.125,
};

export const RAIL_CTA_REM: Record<StorefrontSizeToken, number> = {
  compact: 0.75,
  medium: 0.875,
  large: 1,
};

export const RAIL_MICRO_REM: Record<StorefrontSizeToken, number> = {
  compact: 0.5625,
  medium: 0.625,
  large: 0.6875,
};

export const RAIL_PAD_REM: Record<StorefrontSizeToken, number> = {
  compact: 0.4375,
  medium: 0.625,
  large: 0.75,
};

export const RAIL_ROW_GAP_REM: Record<StorefrontSizeToken, number> = {
  compact: 0.1875,
  medium: 0.4375,
  large: 0.5,
};

/**
 * How much the card shrinks with photo off: photo width plus the gap that
 * separated it from text. Fed to {@link railCardWidth} as `shrink` — so hiding
 * the photo means "more items per view", not only "photo gone".
 */
export function railCardShrinkRem(
  size: StorefrontSizeToken,
  imageVisible: boolean,
): number {
  return imageVisible ? 0 : RAIL_CARD_PHOTO_REM[size] + RAIL_GAP_REM;
}

/**
 * Redistribution ceiling. Stops an absurd `shrink` (corrupt data, photo wider
 * than the card) from becoming a row of slivers: near-zero `reference` would
 * explode the division. Twice the most compact per-view is the plausible cap.
 */
const RAIL_MAX_PER_VIEW = RAIL_PER_VIEW.compact * 2;

/**
 * Fraction of rail width each COUPON ART occupies.
 *
 * Coupons do not split the column like item cards: art is one piece and the
 * carousel is centered — middle full, neighbors clipped. Hence a fraction, not
 * "how many fit".
 *
 * The whole scale stepped DOWN one notch: `large` is the old `max-w-[455px]` on
 * a 900px column (and mobile `w-[85vw]`), formerly `medium`. Coupons support
 * the home; the widest piece there competed with the menu that sells.
 */
export const COUPON_WIDTH_RATIO: Record<
  "desktop" | "mobile",
  Record<StorefrontSizeToken, number>
> = {
  desktop: { compact: 0.26, medium: 0.34, large: 0.4446 },
  mobile: { compact: 0.58, medium: 0.71, large: 0.85 },
};

export type RailDevice = "desktop" | "mobile";

export type RailLayoutInput = {
  device: RailDevice;
  size: StorefrontSizeToken;
  /**
   * FIT — width where a card fits WHOLE: rail minus BOTH gutters. Not CLIP
   * (one gutter), where overflow actually scissors: the left gutter pushes
   * cards; the right does not clip. Confusing them misses by one full gutter —
   * exactly whether the last card is a peek or whole.
   */
  fitWidth: number;
  /**
   * How much the card shrinks without the photo, in px (photo width + gap).
   * Zero when the photo stays.
   */
  shrink: number;
  /** Px per 1rem at the root. The storefront redefines root to 14. */
  rootFontSize: number;
};

/**
 * Width of each rail card in px.
 *
 * On DESKTOP the rail FILLS: size says how many fit and width comes from the
 * column, so the row ends where the title ends. Without photo the card shrinks
 * and the math REDISTRIBUTES — recount how many fit at the smaller width and
 * divide again. Without that, hiding the photo only left a narrow card and a
 * gap on the right.
 *
 * On MOBILE the rail SCROLLS: width comes from the table, the card intentionally
 * past the clip, and the next card's peek signals more.
 */
export function railCardWidth({
  device,
  size,
  fitWidth,
  shrink,
  rootFontSize,
}: RailLayoutInput): number {
  const gap = RAIL_GAP_REM * rootFontSize;

  if (device === "mobile") {
    return Math.max(1, RAIL_MOBILE_CARD_REM[size] * rootFontSize - shrink);
  }

  /** Width that makes `count` cards fill the column exactly. */
  const fill = (count: number) =>
    Math.floor((fitWidth - (count - 1) * gap) / count);

  const reference = Math.max(1, fill(RAIL_PER_VIEW[size]) - shrink);
  const redistributed = Math.floor((fitWidth + gap) / (reference + gap));
  const count = Math.min(RAIL_MAX_PER_VIEW, Math.max(1, redistributed));

  return Math.max(1, fill(count));
}

/**
 * Fraction of rail width each BENEFIT CARD occupies when the showcase has two
 * or more cards (one card fills the column). Same idea as the coupon fraction
 * — centered pieces, neighbors clipped — but wider: these cards are text, and
 * three sentences on a quarter of the column wrap into a tower. On mobile the
 * next card still peeks so the rail reads as scrollable.
 */
export const BENEFIT_WIDTH_RATIO: Record<
  "desktop" | "mobile",
  Record<StorefrontSizeToken, number>
> = {
  desktop: { compact: 0.32, medium: 0.4, large: 0.49 },
  mobile: { compact: 0.7, medium: 0.82, large: 0.92 },
};
